// src/oura/components/ConnectorConnectModal.tsx
//
// Credential entry for the connectors that are not OAuth: WordPress and Framer.
// Google (gsc / ga4) never reaches here — that flow is a redirect, handled in
// DataConnectors.tsx.
//
// The BE validates these live against the target during the connect call, so a
// wrong password fails HERE, in front of the person who typed it, rather than
// silently at publish time. That is the whole reason this is a form and not a
// fire-and-forget save: its job is to surface the backend's own message.

import { useEffect, useState } from "react";
import { Loader2Icon, XIcon } from "lucide-react";
import { Button } from "../primitives/Button";
import { connectConnector, type AeoConnectPayload, type WixSiteCandidate } from "@service/aeo";

type Field = {
    name: keyof AeoConnectPayload;
    label: string;
    hint?: string;
    type?: "text" | "password";
    /** Cleaned up on submit, never while typing — rewriting the box under
     *  someone's cursor is worse than the paste it would have fixed. */
    normalize?: (raw: string) => string;
    /** Checked on submit against the NORMALIZED value. A message stops the
     *  request and is shown under this field, so a wrong value is named where
     *  it was typed instead of coming back as the server's guess about a
     *  different one. */
    validate?: (normalized: string) => string | null;
};

/**
 * What a person actually pastes into "Site URL", turned into the site root the
 * WordPress REST API lives under.
 *
 * Neither side of the wire trimmed this. The backend only does `rstrip("/")`
 * and prepends a scheme, so a trailing space survived into the request and a
 * pasted admin URL became `…/wp-admin/wp-json/wp/v2/users/me` — a 404 that
 * reads to the user as "my correct URL was rejected".
 */
function normalizeSiteUrl(raw: string): string {
    let value = raw.trim().replace(/\s+/g, "");
    if (!value) return value;
    if (!/^https?:\/\//i.test(value)) value = `https://${value}`;
    try {
        const url = new URL(value);
        // Everything a WordPress user is likely to copy out of their browser
        // bar, reduced to the site root the REST API hangs off.
        url.pathname = url.pathname
            .replace(/\/(wp-admin|wp-login\.php|wp-json)(\/.*)?$/i, "")
            .replace(/\/+$/, "");
        url.search = "";
        url.hash = "";
        return url.toString().replace(/\/+$/, "");
    } catch {
        return value.replace(/\/+$/, "");
    }
}

/**
 * A Framer project id: exactly 20 alphanumerics.
 *
 * Same shape the official `framer-api` SDK enforces (`parseProjectId`, 5.0.0),
 * quoted here rather than imported because the SDK is a publish-time dependency
 * the frontend does not carry.
 */
const FRAMER_PROJECT_ID = /^[A-Za-z0-9]{20}$/;

/**
 * The project id a value refers to, or null if it refers to none.
 *
 * Mirrors the SDK's parser: a bare id, or a URL whose path contains
 * `projects/<slug>` where the slug ends in `--<id>`. Everything else — a
 * published `*.framer.website` address, a custom domain, a project URL with the
 * name but no id — yields nothing, which is exactly the set of values worth
 * refusing before they reach the wire.
 */
function framerProjectId(value: string): string | null {
    if (FRAMER_PROJECT_ID.test(value)) return value;
    let url: URL;
    try {
        url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    } catch {
        return null;
    }
    const segments = url.pathname.split("/").filter(Boolean);
    const at = segments.indexOf("projects");
    const slug = at >= 0 ? segments[at + 1] : undefined;
    if (!slug) return null;
    const id = /^.+--([A-Za-z0-9]+)/.exec(slug)?.[1];
    return id && FRAMER_PROJECT_ID.test(id) ? id : null;
}

/**
 * What a person pastes out of the Framer editor's address bar, reduced to the
 * project URL itself.
 *
 * The editor keeps the selected layer in the query string (`?node=…`), so the
 * copied address almost always carries one. Only the id is sent onward — the
 * backend parses it — but a URL with the query stripped is what the BE stores
 * and shows back, and it is the thing worth keeping tidy.
 */
function normalizeFramerProjectUrl(raw: string): string {
    const value = raw.trim().replace(/\s+/g, "");
    if (!value) return value;
    // A bare id is a legitimate answer and is not a URL. Prepending a scheme
    // would turn it into `https://<id>` and quietly change what was typed.
    if (FRAMER_PROJECT_ID.test(value)) return value;
    try {
        const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
        url.search = "";
        url.hash = "";
        return url.toString().replace(/\/+$/, "");
    } catch {
        return value.replace(/\/+$/, "");
    }
}

/**
 * Every failure ourteam-backend#4758 names, and what to do about each.
 *
 * The BE sends its own sentence too, and for most connectors that is the better
 * one — it knows what the third party actually said. Wix is the exception worth
 * overriding: its failures are nearly all fixable by the person at the keyboard,
 * and each fix is a different place to go. `wix_scope_missing` above all — it is
 * the likely first attempt, because a key minted without thinking about scopes
 * authenticates perfectly and then cannot read a single SEO tag.
 *
 * A code this map has never heard of falls through to the server's message
 * rather than a house string: an unknown failure explained badly by us is worse
 * than one explained plainly by whoever saw it.
 */
const WIX_ERRORS: Record<string, string> = {
    wix_auth_failed:
        "Wix did not accept that API key. Check it was pasted whole, and that it has not been revoked in the Wix account.",
    /**
     * Named as the UI names them: **SEO** and **Wix Blog**, the row labels on the
     * permissions screen — not the scope constants.
     *
     * The backend's own message quotes `SCOPE.PROMOTE.MANAGE-SEO`, but nothing on
     * that screen says "Manage SEO". It is one row called `SEO`, under All site
     * permissions, among about a hundred others. A message naming a string the
     * reader cannot find on the page they are staring at is worse than no
     * message.
     *
     * Query Sites, the other call this connector makes, needs
     * `SCOPE.ACC-DC-OS.READ-SITE` — that one has no checkbox at all. It arrives
     * with Basic permissions, which Wix pre-ticks and will not let you deselect,
     * so it is never the thing to go looking for.
     *
     * Wix Blog is genuinely required, and this comment used to say the opposite.
     *
     * The claim was that only `STATIC_PAGE` is ever touched, read off
     * `aeo_wix.py` — true of the SEO-tags path, and not the whole story. Copy
     * changes to a blog post go through `aeo_wix_blog.py`, whose own header
     * says it plainly: "Reading a post needs SCOPE.DC-BLOG.READ-BLOGS and
     * writing one needs SCOPE.DC-BLOG.MANAGE-BLOG", both granted by the single
     * `Wix Blog` row. Its 403 message names that row too.
     *
     * So a key with SEO alone connects and then fails on the first blog post,
     * which is how this surfaced. Left here because a wrong comment about which
     * permissions a client must hand over is worse than none.
     */
    wix_scope_missing:
        "That key authenticates, but it cannot reach your site's SEO settings. In your Wix account settings, open API Keys, edit this key and check it has both SEO and Wix Blog under All site permissions, then paste it here again.",
    wix_no_matching_site:
        "No site on that account serves this business's domain. Check the account ID, or connect from the account that owns the site.",
    wix_multiple_matching_sites:
        "This account has more than one site on that domain. Choose which one Oura should publish to.",
    wix_timeout:
        "Wix did not answer in time. Nothing was saved — try again in a moment.",
};

/** What each credential-based connector asks for, in the order the BE checks it. */
const FIELDS: Record<string, { title: string; help: string; notice?: string; fields: Field[] }> = {
    wordpress: {
        title: "Connect WordPress",
        help: "Oura publishes approved fixes through the WordPress REST API. Use an application password, not the account password — you can revoke it at any time from the same screen.",
        fields: [
            {
                name: "site_url",
                label: "Site URL",
                hint: "The WordPress address, e.g. https://example.com — use the same host the site actually serves on (with or without www).",
                normalize: normalizeSiteUrl,
            },
            { name: "username", label: "Username", normalize: (v) => v.trim() },
            {
                name: "application_password",
                label: "Application password",
                hint: "WordPress admin → Users → Profile → Application Passwords",
                type: "password",
                // Spaces are how WordPress displays the generated password;
                // they are not part of it.
                normalize: (v) => v.replace(/\s+/g, ""),
            },
        ],
    },
    framer: {
        title: "Connect Framer",
        // The two values live in different places, and saying otherwise sent
        // people to a screen the project URL is not on (#3697).
        help: "Framer publishes through its project API. The two values come from different screens: the project URL from the editor's address bar, the API key from Site Settings.",
        fields: [
            {
                name: "project_url",
                label: "Project URL",
                hint: "Open the project in the Framer editor and copy the browser address, e.g. https://framer.com/projects/Website--aabbccddeeff11223344",
                normalize: normalizeFramerProjectUrl,
                validate: (v) =>
                    framerProjectId(v)
                        ? null
                        : "That doesn't look like a Framer project URL. Open the project in Framer and copy the address from the browser bar.",
            },
            {
                name: "api_key",
                label: "API key",
                hint: "Site Settings → General → API Keys → Add",
                type: "password",
                normalize: (v) => v.trim(),
            },
        ],
    },
    wix: {
        title: "Connect Wix",
        help: "Oura writes meta descriptions and structured data through the Wix API. The key needs two permissions — SEO and Wix Blog — and has to be minted by an account owner or co-owner: a site admin cannot create one.",
        /**
         * Stated in the form, at full size, because it is the thing the person
         * pasting is actually agreeing to. A tooltip would be a way of not
         * quite saying it (#3691).
         *
         * It used to read "Wix has no way to narrow it", which the epic recorded
         * as an accepted trade-off. That is no longer true: a Wix key carries
         * TWO independent dimensions, scopes and **site access**, and Wix's own
         * documentation says to "restrict each key to the narrowest set of sites
         * its integration needs". Telling someone their only option is an
         * account-wide key, at the moment they hand one over, talked them out of
         * the safer choice.
         */
        notice: "A new key defaults to every site on the account. Under Sites, switch to Specific sites and pick this one — Oura only ever writes here.",
        fields: [
            {
                name: "api_key",
                label: "API key",
                // Wix's own wording is "Account Settings", and the screen is
                // called the API Keys Manager. The "shown once" warning is the
                // half people lose money on: close the dialog without copying
                // and the only way forward is minting another key.
                // Both sit under All site permissions, adjacent in the list, so
                // naming the section once is enough. "leave the rest" stays: that
                // screen is about a hundred rows and the instruction people need
                // most is which ones NOT to tick.
                hint: "Wix dashboard → Account Settings → API Keys → Generate API Key. Under Permissions, tick SEO and Wix Blog (both inside All site permissions) and leave the rest — Basic permissions is already on and covers finding your site. Wix shows the key once, so copy it before closing that dialog.",
                type: "password",
                normalize: (v) => v.trim(),
            },
            {
                name: "account_id",
                label: "Account ID",
                // Deliberately not "above the key list": where exactly it sits
                // on the screen is the part that rots first.
                hint: "On the same API Keys screen in your Wix account settings.",
                normalize: (v) => v.trim(),
            },
        ],
    },
};

export function ConnectorConnectModal({
    businessId,
    connectorKey,
    onClose,
    onConnected,
}: {
    businessId: string;
    /** `null` closes the modal. Only credential-based keys ever open it. */
    connectorKey: string | null;
    onClose: () => void;
    onConnected: (detail: string) => void;
}) {
    const spec = connectorKey ? FIELDS[connectorKey] : undefined;
    const [values, setValues] = useState<AeoConnectPayload>({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Per-field, so a bad project URL is complained about under the project URL
    // rather than in the box where the server's answers appear.
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    /**
     * The sites to choose between, when the account turned out to hold more than
     * one on this domain (`wix_multiple_matching_sites`).
     *
     * Null until that happens. While it is set the form is replaced rather than
     * appended to: the credentials were accepted, so re-showing them invites
     * someone to change the thing that already worked.
     */
    const [candidates, setCandidates] = useState<WixSiteCandidate[] | null>(null);
    const [chosenSiteId, setChosenSiteId] = useState<string | null>(null);

    // Never carry one connector's half-typed credentials into another's form.
    useEffect(() => {
        setValues({});
        setError(null);
        setFieldErrors({});
        setCandidates(null);
        setChosenSiteId(null);
        setSubmitting(false);
    }, [connectorKey]);

    if (!connectorKey || !spec) return null;

    const missing = spec.fields.filter((f) => !String(values[f.name] ?? "").trim());
    const canSubmit = missing.length === 0 && !submitting;

    /**
     * `extra` is how the site picker resubmits: same credentials, plus the id
     * the person chose. Kept out of `values` so the choice cannot survive into
     * a later attempt with a different account — a stale `site_id` would connect
     * a site nobody picked this time.
     */
    const submit = async (extra?: Partial<AeoConnectPayload>) => {
        if (!canSubmit && !extra) return;
        // Clean up here, at the boundary, so what reaches the BE is what the
        // person meant rather than what their clipboard held.
        const payload: AeoConnectPayload = {};
        for (const f of spec.fields) {
            const raw = String(values[f.name] ?? "");
            payload[f.name] = f.normalize ? f.normalize(raw) : raw.trim();
        }
        // Checked before anything leaves the browser. A value the connector
        // cannot possibly accept should be named here, as itself: Framer
        // answers a malformed project URL by rejecting the API KEY, which sends
        // people off to regenerate a key that was never the problem (#3697).
        const problems: Record<string, string> = {};
        for (const f of spec.fields) {
            const message = f.validate?.(String(payload[f.name] ?? ""));
            if (message) problems[String(f.name)] = message;
        }
        setFieldErrors(problems);
        if (Object.keys(problems).length > 0) return;

        setSubmitting(true);
        setError(null);
        const result = await connectConnector(businessId, connectorKey, { ...payload, ...extra });
        setSubmitting(false);
        if (result.ok && result.kind === "connected") {
            onConnected(result.detail);
            return;
        }
        if (!result.ok) {
            // More than one site on this domain is a QUESTION, not a failure:
            // the credentials worked. Only offer the picker when the server
            // actually sent something to pick from — the copy below covers the
            // case where it says "several" and names none.
            if (result.code === "wix_multiple_matching_sites" && result.sites?.length) {
                setCandidates(result.sites);
                setChosenSiteId(result.sites[0]?.site_id ?? null);
                setError(null);
                return;
            }
            setError(
                (result.code && WIX_ERRORS[result.code])
                || result.message,
            );
            return;
        }
        // A redirect here would mean the catalog mislabelled a Google connector
        // as credential-based; say so rather than silently doing nothing.
        setError("This connector expects a browser sign-in, not credentials. Please report this.");
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-xl">
                <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                    <h2 className="text-base font-semibold text-gray-900">{spec.title}</h2>
                    <button
                        onClick={onClose}
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        aria-label="Close"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-4 px-5 py-4">
                    <p className="my-0 text-sm leading-relaxed text-gray-500">
                        {candidates ? "The key works. This account has more than one site on this domain, so pick the one Oura should publish to." : spec.help}
                    </p>

                    {!candidates && spec.notice && (
                        <p className="my-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            {spec.notice}
                        </p>
                    )}

                    {candidates?.map((site) => (
                        <button
                            key={site.site_id}
                            type="button"
                            onClick={() => setChosenSiteId(site.site_id)}
                            className={`block w-full rounded-md border px-3 py-2 text-left ${
                                chosenSiteId === site.site_id
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-300 bg-white hover:bg-gray-50"
                            }`}
                        >
                            <span className="block text-sm font-medium text-gray-900">
                                {site.site_display_name || "Untitled site"}
                            </span>
                            {/* The id is what actually gets sent, so it is shown:
                                two sites on one domain often differ by nothing a
                                person can see, and picking the wrong one publishes
                                to a site the client is not looking at. */}
                            <span className="mt-0.5 block font-mono text-xs text-gray-400">
                                {site.domain ? `${site.domain} · ` : ""}{site.site_id}
                            </span>
                        </button>
                    ))}

                    {!candidates && spec.fields.map((f) => (
                        <label key={String(f.name)} className="block">
                            <span className="mb-1 block text-xs font-medium text-gray-700">{f.label}</span>
                            <input
                                type={f.type ?? "text"}
                                value={String(values[f.name] ?? "")}
                                onChange={(e) => {
                                    setValues((v) => ({ ...v, [f.name]: e.target.value }));
                                    // Retyping is the answer to the complaint;
                                    // leaving it up argues with the correction.
                                    setFieldErrors((prev) => {
                                        const key = String(f.name);
                                        if (!(key in prev)) return prev;
                                        const next = { ...prev };
                                        delete next[key];
                                        return next;
                                    });
                                }}
                                className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-blue-500"
                                autoComplete="off"
                            />
                            {fieldErrors[String(f.name)] ? (
                                <span className="mt-1 block text-xs text-rose-600">{fieldErrors[String(f.name)]}</span>
                            ) : (
                                f.hint && <span className="mt-1 block text-xs text-gray-400">{f.hint}</span>
                            )}
                        </label>
                    ))}

                    {error && (
                        <p className="my-0 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                            {error}
                        </p>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
                    <Button variant="ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() =>
                            void (candidates
                                ? chosenSiteId && submit({ site_id: chosenSiteId })
                                : submit())
                        }
                        disabled={candidates ? !chosenSiteId || submitting : !canSubmit}
                    >
                        {submitting ? (
                            <>
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                Checking…
                            </>
                        ) : candidates ? (
                            "Connect this site"
                        ) : (
                            "Connect"
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/** Which connectors go through this modal rather than an OAuth redirect. */
export const CREDENTIAL_CONNECTORS = new Set(Object.keys(FIELDS));
